import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Star, ExternalLink, Briefcase, Home, Pencil, FileText } from 'lucide-react'
import { useStore, FEE_CONFIG } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { showToast } from '../components/Toast'
import ShareButtonsRow from '../components/ShareButtonsRow'
import { genreOf, usesReceiptWord } from '../lib/genre'
import { PAYMENT_METHOD_POINTS, WEB3_JPYC_PAYMENT } from '../lib/paymentMethods'

function formatDate(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[date.getMonth()]
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${month} ${day}, ${year} ${hours}:${minutes}`
}

// Category keys shown to user (each maps to an i18n key)
const EXPENSE_CATEGORY_KEYS = ['meals', 'supplies', 'entertainment', 'meeting', 'travel', 'other'] as const
type ExpenseCategoryKey = typeof EXPENSE_CATEGORY_KEYS[number]

// #1661 業態から経費区分を提案する（無料・ルールベース）。
// category は飲食の細分類なので、業態の判定には使えない
function suggestCategory(genre?: string): ExpenseCategoryKey {
  if (genre === 'food') return 'meals'
  if (genre === 'souvenir') return 'supplies'
  return 'other'
}

export default function PaymentComplete() {
  const navigate = useNavigate()
  const { lastPaymentAmount, lastPaymentMethod, currentStore, wallet, transactions, lastPaymentTxHash, lastPaymentExplorerLink, lastPaymentTxId, lastPaymentOrderIds, refreshTransactions, setExpenseTag, budgetEnabled, budgetAmount, getBudgetSpending, budgetNotified80, budgetNotified100, markBudgetNotified } = useStore()
  const { t } = useI18n()

  // #1397 現金会計の取引は店舗が受領を確定した時に**サーバ側で**作られるため、
  // ローカル state には載っていない。掴めていない時だけ 1 回取り直す。
  // (カード決済は楽観追記済みなので取り直さない — 置き換えで消すと本末転倒)
  const refreshedRef = useRef(false)
  useEffect(() => {
    if (refreshedRef.current) return
    refreshedRef.current = true
    const store = useStore.getState()
    const alreadyHave = store.lastPaymentTxId
      ? store.transactions.some((tx) => tx.id === store.lastPaymentTxId)
      : false
    if (!alreadyHave) void refreshTransactions()
  }, [refreshTransactions])

  // Budget alert check (useRef to prevent strict mode double-fire)
  const budgetAlertFired = useRef(false)
  useEffect(() => {
    if (budgetAlertFired.current) return
    if (!budgetEnabled || budgetAmount <= 0) return
    const spending = getBudgetSpending()
    const pct = (spending / budgetAmount) * 100

    if (pct >= 100 && !budgetNotified100) {
      budgetAlertFired.current = true
      showToast({
        type: 'error',
        message: t('budget.exceeded', { total: spending.toLocaleString(), budget: budgetAmount.toLocaleString() }),
        action: { label: t('budget.settings'), onClick: () => navigate('/travel-budget') },
        duration: 8000,
      })
      markBudgetNotified(100)
    } else if (pct >= 80 && !budgetNotified80) {
      budgetAlertFired.current = true
      showToast({
        type: 'info',
        message: t('budget.warning80', { total: spending.toLocaleString(), budget: budgetAmount.toLocaleString() }),
        action: { label: t('budget.check'), onClick: () => navigate('/travel-budget') },
        duration: 8000,
      })
      markBudgetNotified(80)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #1397 今回の会計の取引。**配列の位置で選ばない** — サーバ由来は createdAt DESC、
  // ローカル追記は末尾で並びが混在しており、末尾は「直近 50 件のうち最も古い取引」に
  // なる (7〜8 回前のレシートが出ていた原因)。決済時に控えた ID / 注文 ID で引く。
  const latestTx = useMemo(() => {
    if (lastPaymentTxId) {
      return transactions.find((tx) => tx.id === lastPaymentTxId) ?? null
    }
    // 現金会計は client が取引 ID を知らないので、締められた注文 ID で突き合わせる。
    if (lastPaymentOrderIds.length > 0) {
      const settled = new Set(lastPaymentOrderIds)
      return transactions.find((tx) => tx.orderIds?.some((id) => settled.has(id))) ?? null
    }
    // どちらも無い = 支払いコンテキストが失われている (リロード等)。ここで
    // 推測すると別の会計のレシートを出すことになるので、出さない。
    return null
  }, [transactions, lastPaymentTxId, lastPaymentOrderIds])
  // 店舗一覧キャッシュが空の経路 (#1397) では currentStore が id だけの partial に
  // なりうる。取引側のスナップショットを次点に置き、既定文言は最後の砦にする。
  const storeName = currentStore?.name || latestTx?.storeName || 'うなぎ 平塚屋'
  // #1207 「レシートはアプリで見られる/紙は不要」の案内は最初の3回の支払いのみ。
  // 判定はサーバ由来の取引履歴の件数 — localStorage と違いブラウザのデータ削除で
  // 復活しない。リピーターには出ない (常設導線は下のボタンと取引履歴タブ)。
  const showReceiptNote = transactions.filter((tx) => tx.type === 'payment').length <= 3
  const txId = latestTx?.id || '---'
  const txDate = latestTx?.timestamp ? formatDate(new Date(latestTx.timestamp)) : '---'
  const newBalance = wallet?.balanceJPYC ?? 0

  const amount = lastPaymentAmount
  const chargeRate = FEE_CONFIG.charge.rate
  const chargeFee = FEE_CONFIG.charge.calculate(amount).fee

  // Expense tagging state
  // #1661 業態で提案する。category は飲食の細分類なので 'shop' はもう入らない
  const suggested = suggestCategory(currentStore ? genreOf(currentStore) : undefined)
  const [tagChoice, setTagChoice] = useState<'business' | 'personal' | null>(null)
  const [category, setCategory] = useState<ExpenseCategoryKey>(suggested)
  const [memo, setMemo] = useState('')
  const [tagSaved, setTagSaved] = useState(false)

  function handleSaveTag(tag: 'business' | 'personal') {
    if (!latestTx) return
    setExpenseTag(latestTx.id, tag, tag === 'business' ? category : undefined, tag === 'business' ? memo : undefined)
    setTagSaved(true)
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-warm-white safe-top safe-bottom">
      {/* Top spacer */}
      <div className="flex-1" />

      {/* Success Animation */}
      <div className="relative flex items-center justify-center">
        {/* Pulse ring */}
        <div
          className="absolute h-24 w-24 rounded-full bg-matcha-light/40"
          style={{ animation: 'pulse-ring 1.5s ease-out infinite' }}
        />
        {/* Main circle */}
        <div className="animate-scale-in flex h-24 w-24 items-center justify-center rounded-full bg-matcha ring-4 ring-matcha-light">
          <Check size={48} className="text-white" strokeWidth={3} />
        </div>
      </div>

      {/* Title */}
      <h1
        className="animate-fade-in mt-6 font-display text-2xl font-bold text-text-primary"
        style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
      >
        {t('payment.complete')}
      </h1>

      {/* Amount */}
      <p
        className="animate-fade-in mt-2 font-display text-4xl font-bold text-text-primary"
        style={{ animationDelay: '0.4s', animationFillMode: 'both' }}
      >
        &yen; {lastPaymentAmount.toLocaleString()}
      </p>

      {/* Store name */}
      <p
        className="animate-fade-in mt-1 font-body text-sm text-text-secondary"
        style={{ animationDelay: '0.5s', animationFillMode: 'both' }}
      >
        {t('payment.paidTo', { store: storeName })}
      </p>

      {/* Transaction Detail Card */}
      <div
        className="animate-fade-in mx-4 mt-6 w-full max-w-sm rounded-xl bg-gray-50 p-4"
        style={{ animationDelay: '0.6s', animationFillMode: 'both' }}
      >
        <div className="flex flex-col gap-2">
          {lastPaymentTxHash ? (
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">
                {t('payment.txHash')}
              </span>
              <span className="font-mono text-xs text-navy">
                {lastPaymentTxHash.slice(0, 10)}...{lastPaymentTxHash.slice(-6)}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">
                {t('payment.txId')}
              </span>
              <span className="font-mono text-sm text-text-primary">
                {txId}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="font-body text-sm text-text-secondary">{t('payment.date')}</span>
            <span className="font-body text-sm text-text-primary">
              {txDate}
            </span>
          </div>
          {/* 残高が減る支払い: ポイント払いと、Web3 の実験の JPYC (本物のトークン) */}
          {lastPaymentMethod === PAYMENT_METHOD_POINTS || lastPaymentMethod === WEB3_JPYC_PAYMENT ? (
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">
                {t('payment.newBalance')}
              </span>
              <span className="font-display text-sm font-semibold text-text-primary">
                &yen; {newBalance.toLocaleString()}
              </span>
            </div>
          ) : (
            // #968 現金(cash)もここで決済方法を表示（JPYC残高表示に落とさない）。
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">
                {t('payment.date')}
              </span>
              <span className="font-body text-sm font-medium text-navy">
                {lastPaymentMethod === 'cash' ? t('payment.methodCash') : t('payment.methodStripe')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* YOKOSO PAY user fee */}
      {amount > 0 && (
        <div
          className="animate-fade-in mx-4 mt-4 w-full max-w-sm rounded-xl bg-gray-50 p-4"
          style={{ animationDelay: '0.7s', animationFillMode: 'both' }}
        >
          <div className="flex items-center justify-between">
            <span className="font-body text-xs text-text-secondary">
              {t('distribution.userFee')} ({(chargeRate * 100).toFixed(1)}%)
            </span>
            <span className="font-mono text-xs font-medium text-text-primary">
              &yen;{chargeFee.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Block Explorer Button (on-chain tx only) */}
      {lastPaymentExplorerLink && (
        <div
          className="animate-fade-in mx-4 mt-4 w-full max-w-sm"
          style={{ animationDelay: '0.8s', animationFillMode: 'both' }}
        >
          <a
            href={lastPaymentExplorerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="tap-effect flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-navy/20 bg-navy/5 font-body text-sm font-medium text-navy transition-colors hover:bg-navy/10"
          >
            <ExternalLink size={16} />
            {t('payment.viewOnExplorer')}
          </a>
        </div>
      )}

      {/* Expense Tag Card — payment only */}
      {amount > 0 && latestTx && (
        <div
          className="animate-fade-in mx-4 mt-4 w-full max-w-sm rounded-xl border border-gray-200 bg-white p-4"
          style={{ animationDelay: '0.85s', animationFillMode: 'both' }}
        >
          {tagSaved ? (
            /* Saved state: compact display */
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">{t('expense.label')}</span>
              <button
                onClick={() => setTagSaved(false)}
                className="flex items-center gap-1 text-text-secondary"
              >
                <span className={`font-body text-sm font-medium ${tagChoice === 'business' ? 'text-navy' : 'text-text-tertiary'}`}>
                  {tagChoice === 'business' ? t('expense.business') : t('expense.personal')}
                </span>
                <Pencil size={11} className="text-text-disabled" />
              </button>
            </div>
          ) : (
            <>
              <p className="font-display text-sm font-semibold text-text-primary mb-3">
                {t('expense.prompt')}
              </p>
              {/* Business / Personal toggle */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setTagChoice('business')}
                  className={`flex flex-1 items-center justify-center gap-1.5 h-10 rounded-lg border-2 font-body text-sm font-medium transition-colors ${
                    tagChoice === 'business' ? 'border-navy bg-navy text-white' : 'border-gray-200 text-text-secondary hover:border-navy/40'
                  }`}
                >
                  <Briefcase size={14} />
                  {t('expense.business')}
                </button>
                <button
                  onClick={() => handleSaveTag('personal')}
                  className={`flex flex-1 items-center justify-center gap-1.5 h-10 rounded-lg border-2 font-body text-sm font-medium transition-colors ${
                    tagChoice === 'personal' ? 'border-matcha bg-matcha text-white' : 'border-gray-200 text-text-secondary hover:border-matcha/40'
                  }`}
                >
                  <Home size={14} />
                  {t('expense.personal')}
                </button>
              </div>

              {/* Business detail: category + memo */}
              {tagChoice === 'business' && (
                <>
                  <p className="font-body text-xs text-text-tertiary mb-2">{t('expense.category')}</p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {EXPENSE_CATEGORY_KEYS.map((key) => (
                      <button
                        key={key}
                        onClick={() => setCategory(key)}
                        className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                          category === key
                            ? 'border-navy bg-navy text-white'
                            : 'border-gray-200 text-text-secondary hover:border-navy/40'
                        }`}
                      >
                        {t(`expense.cat.${key}`)}
                        {key === suggested && category === suggested && (
                          <span className="ml-1 text-[9px] opacity-60">★</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="font-body text-xs text-text-tertiary mb-1">{t('expense.memo')}</p>
                  <input
                    type="text"
                    placeholder={t('expense.memoPlaceholder')}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    className="mb-3 w-full h-9 rounded-lg border border-gray-200 px-3 font-body text-sm text-text-primary focus:border-navy focus:outline-none"
                  />
                  <button
                    onClick={() => handleSaveTag('business')}
                    className="w-full h-10 rounded-lg bg-navy font-body text-sm font-medium text-white transition-colors hover:bg-navy/90"
                  >
                    {t('expense.save')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Bottom spacer */}
      <div className="flex-1" />

      {/* Buttons */}
      <div
        className="animate-fade-in w-full px-4 pb-6"
        style={{ animationDelay: '0.7s', animationFillMode: 'both' }}
      >
        <button
          onClick={() => navigate('/review')}
          className="tap-effect flex h-14 w-full items-center justify-center gap-2 rounded-xl border-2 border-yokoso-red bg-white font-display text-base font-semibold text-yokoso-red transition-colors hover:bg-red-50"
        >
          <Star size={20} />
          {t('payment.rateVisit')}
        </button>
        {currentStore && (
          <div className="mt-3">
            <ShareButtonsRow
              storeId={currentStore.id}
              storeName={currentStore.name}
              snsMedia={currentStore.snsMedia}
              source="payment_complete"
            />
          </div>
        )}
        {/* #1207 レシート (明細) / #1168 Web領収書 — 「レシートはアプリで見られる」の
            一次導線。紙は希望者のみ運用の客側アナウンスを兼ねる */}
        {latestTx && (
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => navigate(`/receipt/${latestTx.id}?view=receipt`)}
              className="tap-effect flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white font-body text-sm font-medium text-text-secondary transition-colors hover:bg-gray-50"
            >
              <FileText size={16} />
              {t(usesReceiptWord(currentStore ? genreOf(currentStore) : undefined) ? 'history.viewReceipt' : 'history.viewReceiptStatement')}
            </button>
            <button
              onClick={() => navigate(`/receipt/${latestTx.id}`)}
              className="tap-effect flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white font-body text-sm font-medium text-text-secondary transition-colors hover:bg-gray-50"
            >
              <FileText size={16} />
              {t('receipt.issueButton')}
            </button>
          </div>
        )}
        {latestTx && showReceiptNote && (
          <p className="mt-2 text-center font-body text-[11px] text-text-tertiary">
            {t('payment.receiptAlwaysAvailable')}
          </p>
        )}
        <button
          onClick={() => navigate('/home')}
          className="tap-effect mt-3 flex h-14 w-full items-center justify-center rounded-xl bg-navy font-display text-base font-semibold text-white transition-colors hover:bg-navy-dark"
        >
          {t('payment.done')}
        </button>
      </div>
    </div>
  )
}
